import React, { useEffect, useState, FunctionComponent } from 'react';

import { determineUGTLocale } from '@components/asset/modal/tabs/edit/helpers';
import { getCustomFieldKeys } from '@api/v4/resources/custom_fields';
import { CustomFieldKeyClient } from '@api/v4/resources/CustomFieldKeysTypes';
import { BFLoader } from '@components/common/loader/main';
import { CustomFieldsForm } from './CustomFieldsForm';

export interface DisplayedCustomFieldKey extends CustomFieldKeyClient {
  key?: string;
  multiValuePersisted?: boolean;
}

interface CustomFieldsTabProps {
  brandfolderKey: string;
  controlledCustomFieldsEnabled: boolean;
  hasCustomFieldsFeature: boolean;
  hasRolloutMultiValueCustomFieldsFeature: boolean;
  brandfolderName?: string;
  libraryName?: string;
}

type UpdateControlledCustomFieldsToggledEvent = Event & { detail: { toggled: boolean } }

export const CustomFieldsTab: FunctionComponent<CustomFieldsTabProps> = ({
  brandfolderKey,
  brandfolderName,
  controlledCustomFieldsEnabled,
  hasCustomFieldsFeature,
  hasRolloutMultiValueCustomFieldsFeature,
  libraryName
}) => {
  const [controlledCustomFieldsToggled, setControlledCustomFieldsToggled] = useState(controlledCustomFieldsEnabled);
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState<DisplayedCustomFieldKey[]>([]);

  const params = {
    fields: 'multi_value_enabled,multi_value_peristed',
    order: 'asc',
    sort_by: 'name',
    ugt_locale: determineUGTLocale()
  };

  const getCustomFieldKeysOptions = {
    params,
    resourceKey: brandfolderKey,
    resourceType: 'brandfolder',
  };

  const fetchCustomFieldKeys = async (): Promise<void> => {
    try {
      const response = await getCustomFieldKeys(getCustomFieldKeysOptions);
      const customFieldKeysData: CustomFieldKeyClient[] = response.map((key) => ({
        allowedValues: key.attributes.allowed_values,
        key: key.id,
        multiValueEnabled: key.attributes.multi_value_enabled || false,
        name: key.attributes.name,
      }));

      setRows(customFieldKeysData);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateControlledCustomFieldsToggle = (e: UpdateControlledCustomFieldsToggledEvent): void => setControlledCustomFieldsToggled(e.detail.toggled);

  useEffect(() => {
    window.addEventListener('controlledCustomFieldsToggled', handleUpdateControlledCustomFieldsToggle);
    fetchCustomFieldKeys();

    return (): void => {
      window.removeEventListener('controlledCustomFieldsToggled', handleUpdateControlledCustomFieldsToggle);
    };
  }, []);

  const showForm = !isLoading && hasCustomFieldsFeature && controlledCustomFieldsToggled;

  const showControlledCustomFieldsDisabledMessage = !isLoading && hasCustomFieldsFeature && !controlledCustomFieldsToggled;
  const showCustomFieldsFeatureDisabledMessage = !isLoading && !hasCustomFieldsFeature;

  return (
    <div className="brandfolder-settings__control-custom-fields">
      {isLoading && <BFLoader />}
      {showForm && (
        <>
          <CustomFieldsForm
            brandfolderKey={brandfolderKey}
            fetchCustomFieldKeys={fetchCustomFieldKeys}
            hasRolloutMultiValueCustomFieldsFeature={hasRolloutMultiValueCustomFieldsFeature}
            rows={rows}
            setRows={setRows}
          />
          <p className="custom-field-mobile-instructions">{bfTranslate(`To access Custom Field settings, please visit your ${libraryName} on a larger screen.`)}</p>
        </>
      )}
      {showControlledCustomFieldsDisabledMessage && (
        <>
          <h2 className="brandfolder-settings__control-custom-fields--title">{bfTranslate('Custom Fields Settings')}</h2>
          <p className="brandfolder-settings__control-custom-fields--messaging" dangerouslySetInnerHTML={{ __html: bfTranslate(`No settings available because Controlled Custom Fields are <span class="bold-font">disabled</span>. <br/>Admins and Collaborators can input their own keys and values on each asset.`) }} />
          <p className="brandfolder-settings__control-custom-fields--messaging">{bfTranslate(`To enable settings for your Custom Fields, visit the Advanced tab and turn on the "Control Custom Fields" setting.`)}</p>
          <div className="brandfolder-settings__control-custom-fields--example">
            <div className="flex-half">
              <h3 className="brandfolder-settings__control-custom-fields--subtitle">{bfTranslate('What are Custom Fields?')}</h3>
              <p className="brandfolder-settings__control-custom-fields--messaging">{bfTranslate('Custom Fields provide a controlled taxonomy tool by adding key/value pairs to your assets. By enabling Controlled Custom Fields, Admins can specify keys and value options to apply to assets.')}</p>
              <p className="brandfolder-settings__control-custom-fields--messaging">{bfTranslate('It is recommended to enable Controlled Custom Fields.')}</p>
            </div>
            <div className="flex-half">
              <p className="italic-font">{bfTranslate('Example Key/Value Custom Field pairing')}</p>
              <div>
                <img
                  alt="example of custom fields key value pair"
                  className="brandfolder-settings__control-custom-fields--example-image"
                  src="https://cdn.bfldr.com/27C9EC93/at/x4s8tc5xnb6w85n3wqfjx866/custom-fields-example?format=jpg&auto=webp&dpr=2"
                />
              </div>
            </div>
          </div>
        </>
      )}
      {showCustomFieldsFeatureDisabledMessage && (
        <>
          <h2 className="brandfolder-settings__control-custom-fields--title">{bfTranslate('Custom Fields Settings')}</h2>
          <p className="brandfolder-settings__control-custom-fields--messaging">{bfTranslate(`No settings available because your subscription doesn't support Custom Fields.`)}</p>
          <div className="brandfolder-settings__control-custom-fields--example">
            <div className="flex-half">
              <h3 className="brandfolder-settings__control-custom-fields--subtitle">{bfTranslate('What are Custom Fields?')}</h3>
              <p className="brandfolder-settings__control-custom-fields--messaging">{bfTranslate('Custom Fields provide a controlled taxonomy tool by adding key/value pairs to your assets. They help:')}</p>
              <ul className="brandfolder-settings__control-custom-fields--list">
                <li><span className="bff-confirmation" />{bfTranslate('Classify and define your assets')}</li>
                <li><span className="bff-confirmation" />{bfTranslate('Categorize assets')}</li>
                <li><span className="bff-confirmation" />{bfTranslate('Improve searchability')}</li>
              </ul>
              <a className="brandfolder-settings__control-custom-fields--cta" href="mailto:support@brandfolder.com?subject=Upgrade Inquiry&body=I'd like to inquire about adding the Custom Fields feature.">{bfTranslate(`Inquire about adding the Custom Fields feature to the ${brandfolderName} ${libraryName}.`)}</a>
            </div>
            <div className="flex-half">
              <p className="italic-font">{bfTranslate('Example Key/Value Custom Field pairing')}</p>
              <div>
                <img
                  alt={bfTranslate('example of custom fields key value pair')}
                  className="brandfolder-settings__control-custom-fields--example-image"
                  src="https://cdn.bfldr.com/27C9EC93/at/x4s8tc5xnb6w85n3wqfjx866/custom-fields-example?format=jpg&auto=webp&dpr=2"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
